import { yupResolver } from '@hookform/resolvers/yup'
import { EFinancingCompany, EFinancingProgram } from '@if/core/types'
import { InferType } from '@if/core/yup'
import { getErrorMessage } from '@if/mui'
import { Alert, Button, Snackbar, Stack, Tab, Tabs } from '@mui/material'
import { AsyncActionButton } from '@src/components'
import TabPanel from '@src/components/TabPanel'
import { useFinancingConfigs } from '@src/data/api/financing-config-api'
import { useCreateMerchantPaymentPlans, useUpdateMerchant } from '@src/data/api/merchants-api/merchants-api'
import { ApplicableMerchantPaymentPlan } from '@src/data/types/ApplicableMerchantPaymentPlan'
import { EMerchantProperties } from '@src/data/types/Constants'
import { CreateMerchantPaymentPlanDto } from '@src/data/types/CreateMerchantPaymentPlanDto'
import { Merchant } from '@src/data/types/Merchant'
import { MerchantFinancingProgramConfig } from '@src/data/types/MerchantFinancingProgramConfig'
import { MerchantPaymentPlan } from '@src/data/types/MerchantPaymentPlan'
import { reportErrorToConsole } from '@src/services/error-logger'
import { memo, useMemo, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import MerchantBuyDownPanel, { MerchantBuyDownSchema } from './MerchantBuyDownPanel'
import MerchantFinancingProgramConfigsPanel, {
  makeMerchantFinancingProgramConfigsSchema,
} from './MerchantFinancingProgramConfigsPanel'
import MerchantPaymentPlansPanel from './MerchantPaymentPlansPanel'
import MerchantPromoPlanPanel, { MerchantPlanPromoRulesSchema } from './MerchantPromoPlanPanel'
import MerchantPropertiesPanel, { MerchantPropertiesSchema } from './MerchantPropertiesPanel'
import MerchantRatesPanel from './MerchantRatesPanel'
import MerchantReportConfigPanel, { MerchantReportFormSchema } from './MerchantReportConfigPanel'
import MerchantTagsPanel, { MerchantTagsSchema } from './MerchantTagsPanel'
import MerchantTerritory, { MerchantTerritorySchema } from './MerchantTerritory'
import { useMerchantFormSync } from './useMerchantFormSync'

interface Props {
  merchant: Merchant
  shouldShowGeneralTab: boolean
  shouldShowPromotionTab: boolean
  shouldShowProgramConfigsTab: boolean
  merchantPaymentPlans: MerchantPaymentPlan[]
}

const configProptertyList = Object.values(EMerchantProperties)

const MerchantForm = ({
  merchant,
  shouldShowGeneralTab,
  shouldShowPromotionTab,
  shouldShowProgramConfigsTab,
  merchantPaymentPlans,
}: Props) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [updateMerchant, isUpdatingMerchant, error, reset] = useUpdateMerchant()
  const [createMerchantPaymentPlans, isCreatingMerchantPaymentPlans] = useCreateMerchantPaymentPlans()
  const [financingConfigs] = useFinancingConfigs()

  const [tabIndex, setTabIndex] = useState(shouldShowGeneralTab ? 0 : 1)

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue)
  }

  const merchantDefaultProperties = useMemo(
    () =>
      configProptertyList.reduce(
        (acc, curr) => {
          acc[curr] = merchant[curr]
          return acc
        },
        {} as Record<EMerchantProperties, boolean>,
      ),
    [merchant],
  )

  const MerchantFormSchema = useMemo(
    () =>
      MerchantPropertiesSchema.concat(MerchantTagsSchema)
        .concat(MerchantTerritorySchema)
        .concat(MerchantReportFormSchema)
        .concat(MerchantPlanPromoRulesSchema)
        .concat(
          makeMerchantFinancingProgramConfigsSchema(
            financingConfigs,
            merchant.serviceCategory ?? undefined,
          ),
        )
        .concat(MerchantBuyDownSchema),
    [financingConfigs, merchant.serviceCategory],
  )

  const merchantFinancingProgramConfigs = useMemo(() => {
    return Object.values(EFinancingProgram).reduce(
      (obj, program) => {
        const config = merchant.financingProgramConfigs?.find((c) => c.financingProgramId == program)
        if (config) obj[program] = config
        return obj
      },
      {} as Partial<Record<EFinancingProgram, MerchantFinancingProgramConfig>>,
    )
  }, [merchant])

  type MerchantFormSchemaType = InferType<typeof MerchantFormSchema>
  const form = useForm<MerchantFormSchemaType>({
    resolver: yupResolver(MerchantFormSchema),
    mode: 'onBlur',
    defaultValues: {
      tags: merchant.tags ?? [],
      territoryId: merchant.territoryId,
      reportGenerationFrequency: merchant.reportGenerationFrequency,
      reportRecipientsEmailList: merchant.reportRecipientsEmailList,
      planPromoRules: merchant.planPromoRules,
      financingProgramConfigs: merchantFinancingProgramConfigs,
      bannerMerchantFeeRate: merchant.bannerMerchantFeeRate || undefined,
      flashPlanRateRebate: merchant.flashPlanRateRebate,
      lenderAdminFee: merchant.lenderAdminFee,
      ...merchantDefaultProperties,
    },
  })
  const { formState } = form

  useMerchantFormSync(form, merchant, merchantFinancingProgramConfigs, merchantDefaultProperties)

  const [merchantPaymentPlansToCreate, setMerchantPaymentPlansToCreate] = useState(
    [] as ApplicableMerchantPaymentPlan[],
  )

  const applicableMerchantPaymentPlans: ApplicableMerchantPaymentPlan[] = useMemo(() => {
    return [...merchantPaymentPlansToCreate, ...merchantPaymentPlans]
  }, [merchantPaymentPlansToCreate, merchantPaymentPlans])

  const isSubmitDisabled =
    isUpdatingMerchant ||
    isCreatingMerchantPaymentPlans ||
    (!formState.isDirty && merchantPaymentPlansToCreate.length === 0)

  const isHighEstimateError = error?.response?.data === 'Dashboard access required for High Estimate'

  const bannerOwnedWriteGuardFields = useMemo(() => {
    const message = error ? getErrorMessage(error) : null
    if (!message?.includes('owned by the parent banner')) return null
    // Extracts the field list after the last colon, e.g. "Banner owned fields: Foo, Bar." → "Foo, Bar"
    return /:\s*(.+?)\.?\s*$/.exec(message)?.[1] ?? null
  }, [error])

  const errorMessage = isHighEstimateError
    ?  t('common.errors.dashboardAccessRequiredForHighEstimate')
    : bannerOwnedWriteGuardFields !== null
      ? t('merchant.bannerOwnedWriteGuardError', { fields: bannerOwnedWriteGuardFields })
      : null

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const promises: Promise<void>[] = []

    if (merchantPaymentPlansToCreate.length !== 0)
      promises.push(submitMerchantPaymentPlans(merchantPaymentPlansToCreate))

    if (formState.isDirty) {
      promises.push(submitMerchantForm())
    }

    try {
      await Promise.all(promises)
      navigate(`/merchant/${merchant.id}/view`)
    } catch (e) {
      reportErrorToConsole(e)
    }
  }

  const submitMerchantForm = async () => {
    const formIsValid = await form.trigger()
    if (!formIsValid) throw new Error('form has errors')
    await form.handleSubmit(submitMerchant)()
  }

  const submitMerchant = async (data: MerchantFormSchemaType) => {
    await updateMerchant({
      territoryId: data.territoryId,
      noPaymentPolicy: data.noPaymentPolicy,
      hideAddress: data.hideAddress,
      canSkipFlinks: data.canSkipFlinks,
      canPayBorrowers: data.canPayBorrowers,
      automaticIncomeApproval: data.automaticIncomeApproval,
      planPromoRules: data.planPromoRules,
      merchantId: merchant.id,
      tags: data.tags,
      reportGenerationFrequency: !data.reportGenerationFrequency ? 'none' : data.reportGenerationFrequency,
      reportRecipientsEmailList: data.reportRecipientsEmailList,
      canAcceptHighEstimate: data.canAcceptHighEstimate,
      showInterestRateOnMerchantDashboard: data.showInterestRateOnMerchantDashboard,
      rateAgreements: merchant.rateAgreements,
      bannerMerchantFeeRate: data.bannerMerchantFeeRate ?? null,
      flashPlanRateRebate: data.flashPlanRateRebate ?? null,
      lenderAdminFee: data.lenderAdminFee ?? null,
      financingProgramConfigs: Object.entries(data.financingProgramConfigs)
        .filter(([_, values]) => Object.keys(values).length !== 0)
        .map(([program, values]) => {
          return {
            financingProgramId: program as EFinancingProgram,
            ...values,
          }
        }),
    })

    form.reset(data)
  }

  const submitMerchantPaymentPlans = async (createMerchantPaymentPlanDtos: CreateMerchantPaymentPlanDto[]) => {
    await createMerchantPaymentPlans({ merchantId: merchant.id, dtos: createMerchantPaymentPlanDtos })

    setMerchantPaymentPlansToCreate([])
  }

  return (
    <>
      {errorMessage !== null && (
        <Snackbar open autoHideDuration={9000} onClose={() => reset()} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
          <Alert severity="error" onClose={() => reset()}>
            {errorMessage}
          </Alert>
        </Snackbar>
      )}

      <Tabs value={tabIndex} onChange={handleTabChange} variant="fullWidth">
        <Tab label={t('merchantSetting.generalSettings')} disabled={!shouldShowGeneralTab} />
        <Tab label={t('merchantSetting.promotionSettings')} disabled={!shouldShowPromotionTab} />
        <Tab label={t('merchantSetting.financingProgramsSettings')} disabled={!shouldShowProgramConfigsTab} />
      </Tabs>

      <FormProvider {...form}>
        <Stack gap={2} component="form" onSubmit={onSubmit}>
          {shouldShowGeneralTab && (
            <TabPanel value={tabIndex} index={0}>
              <MerchantTerritory merchant={merchant} />
              <MerchantTagsPanel />
              <MerchantRatesPanel merchant={merchant} />
              <MerchantReportConfigPanel />
              <MerchantPropertiesPanel bannerOwnedFields={merchant.bannerOwnedFields ?? []} />
              <MerchantBuyDownPanel bannerOwnedFields={merchant.bannerOwnedFields ?? []} />
            </TabPanel>
          )}
          {shouldShowPromotionTab && (
            <TabPanel value={tabIndex} index={1}>
              {merchant.financingCompanyId == EFinancingCompany.Iceberg && (
                <MerchantPaymentPlansPanel
                  merchant={merchant}
                  merchantPaymentPlans={merchantPaymentPlans}
                  merchantPaymentPlansToCreate={merchantPaymentPlansToCreate}
                  onMerchantPaymentPlanAdded={(dto) => {
                    setMerchantPaymentPlansToCreate([...merchantPaymentPlansToCreate, dto])
                  }}
                />
              )}
              <MerchantPromoPlanPanel
                availablePaymentPlans={applicableMerchantPaymentPlans}
                initialPlanPromoRules={merchant.planPromoRules}
                planPromoRules={form.getValues('planPromoRules')}
                supportFinancingPrograms={merchant.supportedFinancingPrograms}
              />
            </TabPanel>
          )}
          {shouldShowProgramConfigsTab && (
            <TabPanel value={tabIndex} index={2}>
              <MerchantFinancingProgramConfigsPanel
                supportedFinancingPrograms={merchant.supportedFinancingPrograms}
                financingConfigs={financingConfigs}
                serviceCategory={merchant.serviceCategory ?? undefined}
              />
            </TabPanel>
          )}

          <Stack direction="row" justifyContent="end" spacing={2}>
            <Button variant="contained" color="primary" href={`/merchant/${merchant.id}/view`}>
              {t('common.back')}
            </Button>
            <AsyncActionButton
              variant="contained"
              color="primary"
              type="submit"
              isPending={isUpdatingMerchant}
              disabled={isSubmitDisabled}
            >
              {t('common.save')}
            </AsyncActionButton>
          </Stack>
        </Stack>
      </FormProvider>
    </>
  )
}

export default memo(MerchantForm)
